import React from 'react';

import { ErrorBoundary } from '@nxte-nl/error-boundary';

import { FooterBottomPart } from '../../components/FooterBottomPart/FooterBottomPart';
import { FooterLogos } from '../../components/FooterLogos/FooterLogos';
import { FooterMiddlePart } from '../../components/FooterMiddlePart/FooterMiddlePart';
import { FooterTopPart } from '../../components/FooterTopPart/FooterTopPart';
import { featureStateHooks } from '../../hooks/featureStateHooks/featureStateHooks';
import { layoutHooks } from '../../hooks/layoutHooks/layoutHooks';
import { messageHooks } from '../../hooks/messageHooks/messageHooks';
import { AppDataStoreData, RefsMessages } from '../../types/Messages';
import { ImagePresentational } from '../../types/common';
import { LayoutExtensionContainer } from '../LayoutExtensionContainer/LayoutExtensionContainer';

export const COUNT_IMAGES_ON_MOBILE = 4;

export const hooks = {
  useGetFooterLogoImages(
    isMobile: boolean,
    footerImages?: ImagePresentational[],
  ) {
    return React.useMemo(() => {
      if (!footerImages || footerImages.length < 1) {
        return null;
      }

      return isMobile ? footerImages.slice(0, COUNT_IMAGES_ON_MOBILE) : footerImages;
    }, [footerImages, isMobile]);
  },

  useSetFooterRef(footerRef: React.RefObject<HTMLDivElement>) {
    const setFooterRef = messageHooks.useNext(RefsMessages.FooterRef);

    React.useEffect(() => {
      if (footerRef?.current) {
        setFooterRef(footerRef);
      }
    }, [footerRef, setFooterRef]);
  },
};

export type Props = {
  isRedeem?: boolean;
  showTopAndMiddleParts?: boolean;
};

export const FooterContainer = (props: Props) => {
  const {
    showTopAndMiddleParts = true,
    isRedeem = false,
  } = props;

  const storeData = messageHooks.useSubjectState(AppDataStoreData.StoreData);

  const {
    isFooterLogos,
    isLogosFromCBEOn,
    isCookiesFooterLinkOn,
  } = featureStateHooks.useFeatureToggles();

  const isMobile = layoutHooks.useMobileLayout();

  const footerImages = hooks.useGetFooterLogoImages(
    isMobile,
    storeData?.paymentIcons,
  );

  const footerRef = React.useRef<HTMLDivElement | null>(null);

  hooks.useSetFooterRef(footerRef);

  return (
    <footer ref={footerRef}>
      {showTopAndMiddleParts && (
        <>
          <ErrorBoundary>
            <FooterTopPart />
          </ErrorBoundary>

          <ErrorBoundary>
            <FooterMiddlePart
              data={storeData?.menus?.footerMenu?.menuItems}
            />
          </ErrorBoundary>
          {isFooterLogos && footerImages && (
            <ErrorBoundary>
              <FooterLogos footerImages={footerImages}/>
            </ErrorBoundary>
          )}
        </>
      )}

      <ErrorBoundary>
        <LayoutExtensionContainer>
          {({ layoutExtension }) => (
            <FooterBottomPart
              isRedeem={isRedeem}
              layoutExtension={layoutExtension}
              isLogosFromCBEOn={isLogosFromCBEOn}
              footerLogoFromCBE={storeData?.footerLogo}
              isCookiesFooterLink={isCookiesFooterLinkOn}
            />
          )}
        </LayoutExtensionContainer>
      </ErrorBoundary>
    </footer>
  );
};

FooterContainer.displayName = 'FooterContainer';
